import {
  createWorkspaceCatalog,
  defaultWorkspaceColor,
  SAMPLE_JOINED_WORKSPACE,
  SEED_WORKSPACE_META,
  type WorkspaceMembership,
  type WorkspaceTemplate,
} from "@/engine/workspace-seeds";
import { normalizeWorkspaceIcon, normalizeWorkspaceImageUrl } from "@/features/workspace/workspace-face";
import type { WorkspaceDocTemplate } from "@/features/docs/doc-templates";
import type { OrganizationTeam, Project, UserResource } from "@/engine/types";
import type { ProjectTemplate } from "@/features/retrospective/retrospective-model";
import { buildDemoHistory, type ReportPeriod } from "@/features/reports/report-model";
import { attachSampleWeeklyReports } from "@/features/weekly-report/sample-weekly";

export type WorkspaceTab = "schedule" | "reports" | "tasks" | "records" | "docs" | "files" | "calendar" | "team" | "budget" | "stakeholders" | "weekly" | "review" | "activity" | "intake";

export interface WorkspaceChrome {
  checkpoints: Array<{ id: string; name: string; createdAt: string; project: Project }>;
  reports?: ReportPeriod[];
  collapsed: string[];
  ui?: unknown;
  lastTab?: WorkspaceTab;
}

export interface WorkspaceMeta {
  id: string;
  name: string;
  /** 팀 아이콘 id. 이미지가 없을 때 색 타일에 그린다. */
  icon: string;
  color: string;
  /** 대표 이미지. 없으면 색 타일이다. */
  imageUrl: string | null;
  description: string;
  createdAt: string;
  template: WorkspaceTemplate;
  role: WorkspaceMembership;
  /** 참여 워크스페이스의 소유자. 소유 행은 null. */
  ownerName: string | null;
  ownerEmail: string | null;
}

export interface WorkspaceRecord extends WorkspaceMeta {
  projects: Record<string, Project>;
  byProject: Record<string, WorkspaceChrome>;
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
  activeProjectId: string | null;
  /** 상단 크롬에 올려 둔 플랜. 비어 있으면 열린 탭이 없다. */
  openPlanIds?: string[];
  /** 문서 종류별 목차. 시스템 기본 목차는 여기 두지 않는다. */
  docTemplates?: WorkspaceDocTemplate[];
  /** 회고에서 저장한 WBS 템플릿. */
  projectTemplates?: ProjectTemplate[];
}

export interface WorkspaceSummary extends WorkspaceMeta {
  projectCount: number;
  resourceCount: number;
}

export function emptyChromeMap(projectIds: string[]): Record<string, WorkspaceChrome> {
  const byProject: Record<string, WorkspaceChrome> = {};
  for (const id of projectIds) byProject[id] = { checkpoints: [], collapsed: [] };
  return byProject;
}

/** Sample plans come with three closed report periods and two or three weekly reports. */
export function withDemoReports(
  projects: Record<string, Project>,
  resources: Record<string, UserResource>,
): { projects: Record<string, Project>; byProject: Record<string, WorkspaceChrome> } {
  const nextProjects: Record<string, Project> = {};
  const byProject: Record<string, WorkspaceChrome> = {};
  for (const [id, p] of Object.entries(projects)) {
    const { project, periods } = buildDemoHistory(p, resources);
    nextProjects[id] = attachSampleWeeklyReports(project, resources);
    byProject[id] = { checkpoints: [], collapsed: [], reports: periods };
  }
  return { projects: nextProjects, byProject };
}

export function buildWorkspaceRecord(opts: {
  id: string;
  name: string;
  icon?: string;
  color?: string;
  imageUrl?: string | null;
  description?: string;
  template: WorkspaceTemplate;
  createdAt?: string;
  index?: number;
  role?: WorkspaceMembership;
  ownerName?: string | null;
  ownerEmail?: string | null;
}): WorkspaceRecord {
  const data = createWorkspaceCatalog(opts.template);
  const seeded = withDemoReports(data.projects, data.resources);
  const name = opts.name.trim() || "새 워크스페이스";
  return {
    id: opts.id,
    name,
    icon: normalizeWorkspaceIcon(opts.icon),
    color: opts.color || defaultWorkspaceColor(opts.index ?? 0),
    imageUrl: normalizeWorkspaceImageUrl(opts.imageUrl),
    description: opts.description?.trim() ?? "",
    createdAt: opts.createdAt ?? new Date().toISOString(),
    template: opts.template,
    role: opts.role ?? "owner",
    ownerName: opts.ownerName?.trim() || null,
    ownerEmail: opts.ownerEmail?.trim() || null,
    projects: seeded.projects,
    byProject: seeded.byProject,
    resources: data.resources,
    teams: data.teams,
    activeProjectId: data.activeProjectId,
  };
}

export function seedWorkspaceRecords(): Record<string, WorkspaceRecord> {
  const createdAt = "2026-09-01T00:00:00.000Z";
  const workspaces: Record<string, WorkspaceRecord> = {};
  SEED_WORKSPACE_META.forEach((meta, index) => {
    workspaces[meta.id] = buildWorkspaceRecord({
      id: meta.id,
      name: meta.name,
      icon: meta.icon,
      color: meta.color,
      imageUrl: meta.imageUrl,
      description: meta.description,
      template: meta.template,
      createdAt,
      index,
      role: meta.role,
      ownerName: meta.ownerName,
      ownerEmail: meta.ownerEmail,
    });
  });
  const joined = SAMPLE_JOINED_WORKSPACE;
  workspaces[joined.id] = buildWorkspaceRecord({
    id: joined.id,
    name: joined.name,
    icon: joined.icon,
    color: joined.color,
    imageUrl: joined.imageUrl,
    description: joined.description,
    template: joined.template,
    createdAt: "2026-10-02T00:00:00.000Z",
    role: joined.role,
    ownerName: joined.ownerName,
    ownerEmail: joined.ownerEmail,
  });
  return workspaces;
}

export function listWorkspaceSummaries(workspaces: Record<string, WorkspaceRecord>): WorkspaceSummary[] {
  return Object.values(workspaces)
    .map((ws) => ({
      id: ws.id,
      name: ws.name,
      icon: ws.icon,
      color: ws.color,
      imageUrl: ws.imageUrl,
      description: ws.description,
      createdAt: ws.createdAt,
      template: ws.template,
      role: ws.role,
      ownerName: ws.ownerName,
      ownerEmail: ws.ownerEmail,
      projectCount: Object.keys(ws.projects).length,
      resourceCount: Object.keys(ws.resources).length,
    }))
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.name.localeCompare(b.name, "ko")));
}

export function patchWorkspaceMeta(
  workspaces: Record<string, WorkspaceRecord>,
  id: string,
  patch: Partial<Pick<WorkspaceMeta, "name" | "icon" | "color" | "description" | "imageUrl">>,
): Record<string, WorkspaceRecord> | null {
  const cur = workspaces[id];
  if (!cur) return null;
  const name = patch.name !== undefined ? patch.name.trim() || cur.name : cur.name;
  const next: WorkspaceRecord = {
    ...cur,
    name,
    icon: patch.icon !== undefined ? normalizeWorkspaceIcon(patch.icon, cur.icon) : cur.icon,
    color: patch.color !== undefined ? patch.color : cur.color,
    imageUrl: patch.imageUrl !== undefined ? normalizeWorkspaceImageUrl(patch.imageUrl) : cur.imageUrl,
    description: patch.description !== undefined ? patch.description.trim() : cur.description,
  };
  return { ...workspaces, [id]: next };
}

/** Returns null when the workspace is missing or it is the last one. */
export function deleteWorkspaceRecord(
  workspaces: Record<string, WorkspaceRecord>,
  id: string,
): { workspaces: Record<string, WorkspaceRecord>; nextActiveId: string } | null {
  if (!workspaces[id] || Object.keys(workspaces).length <= 1) return null;
  const { [id]: _, ...rest } = workspaces;
  const nextActiveId = Object.keys(rest)[0]!;
  return { workspaces: rest, nextActiveId };
}

export function refillWorkspace(ws: WorkspaceRecord, template: WorkspaceTemplate = ws.template === "empty" ? "erp" : ws.template): WorkspaceRecord {
  const data = createWorkspaceCatalog(template);
  const seeded = withDemoReports(data.projects, data.resources);
  return {
    ...ws,
    template,
    projects: seeded.projects,
    byProject: seeded.byProject,
    resources: data.resources,
    teams: data.teams,
    activeProjectId: data.activeProjectId,
  };
}
